import { useCallback, useEffect, useState } from 'react';
import { produce } from 'immer';
import { isFunction } from 'lodash';

import { getImageOverlayTextFieldAssetType, IMAGE_OVERLAY_TEXT_FIELD } from 'src/data/image';
import { useEffectOnce } from 'src/lib/hooks/useEffectOnce';
import { useScoreText } from 'src/services/useScoreText';
import { useCurrentProjectId } from 'src/store/project';

import { usePageActions } from '../store';

export const useScoredOverlayData = ({ initialData, data, onFieldInputChange }) => {
  const projectId = useCurrentProjectId();
  const pageActions = usePageActions();

  const [scoredData, setScoredData] = useState(() => initialToData(initialData || data));

  const scoreText = useScoreText({ projectId }, (field, error, scoringResult) => {
    if (scoringResult) {
      pageActions.setTextScore(scoringResult.assetType, scoringResult.text, scoringResult.score);
    }

    setScoredData(
      produce((draft) => {
        ensureFieldPresence(draft, field);
        draft[field].score = scoringResult?.score;
        draft[field].loading = false;
      })
    );
  });

  // we might already have a score for this text value in the cache
  const tryCachedScore = useCallback(
    (field, text) => {
      const assetType = getImageOverlayTextFieldAssetType(field);
      const cachedScore = pageActions.getTextScore(assetType, text);
      if (cachedScore != null) {
        setScoredData(
          produce((draft) => {
            ensureFieldPresence(draft, field);
            draft[field].text = text; // make sure the "text" is set in the output data
            draft[field].score = cachedScore;
            draft[field].loading = false;
          })
        );
        return true;
      }

      return false;
    },
    [pageActions]
  );

  const scoreDataObject = useCallback(
    (dataObject) => {
      Object.keys(dataObject).forEach((field) => {
        const text = dataObject[field] || '';
        if (text.length === 0) {
          return;
        }

        if (tryCachedScore(field, text)) {
          return;
        }

        setScoredData(
          produce((draft) => {
            ensureFieldPresence(draft, field);
            draft[field].text = text;
            draft[field].score = null;
            draft[field].loading = true;
          })
        );

        const assetType = getImageOverlayTextFieldAssetType(field);
        scoreText({ index: field, assetType, text });
      });
    },
    [scoreText, tryCachedScore]
  );

  // get initial scores for all "input" fields
  useEffectOnce(() => {
    if (!initialData) {
      return;
    }

    scoreDataObject(initialData);
  });

  // recalculate all scores when the "data" changes
  useEffect(() => {
    if (!data) {
      return;
    }

    scoreDataObject(data);
  }, [scoreDataObject, data]);

  // handle changes in the text fields
  const onFieldChange = useCallback(
    (field, text) => {
      if (tryCachedScore(field, text)) {
        return;
      }

      const assetType = getImageOverlayTextFieldAssetType(field);

      const produceState = (prevState) => {
        const newState = produce(prevState, (draft) => {
          ensureFieldPresence(draft, field);
          draft[field].text = text;
          draft[field].score = null;
          draft[field].loading = true;
        });

        if (isFunction(onFieldInputChange)) {
          onFieldInputChange(dataToOutput(newState));
        }

        return newState;
      };

      setScoredData((prevState) => produceState(prevState));

      scoreText({ index: field, assetType, text });
    },
    [onFieldInputChange, scoreText, tryCachedScore]
  );

  const getOutputData = useCallback(() => {
    return dataToOutput(scoredData);
  }, [scoredData]);

  return { scoredData, onFieldChange, getOutputData };
};

const initialToData = (input) => {
  if (!input) {
    return {};
  }

  return Object.values(IMAGE_OVERLAY_TEXT_FIELD).reduce((acc, field) => {
    if (input?.[field]) {
      ensureFieldPresence(acc, field);
      acc[field].text = input[field];
    }
    return acc;
  }, {});
};

const dataToOutput = (data) =>
  Object.entries(data).reduce((acc, [field, value]) => {
    acc[field] = value.text;
    return acc;
  }, {});

const ensureFieldPresence = (data, field) => {
  if (data[field]) {
    return;
  }

  data[field] = {
    assetType: getImageOverlayTextFieldAssetType(field),
  };
};
